import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styles from "../Home/HomePage.module.css";

export default function PublicMeterKwhAndToken() {
  useEffect(() => {
    const prev = localStorage.getItem("navbar_state") || "";
    localStorage.setItem("navbar_state", "public");
    return () => {
      if (prev) localStorage.setItem("navbar_state", prev);
      else localStorage.removeItem("navbar_state");
    };
  }, []);

  const TURNSTILE_SITE_KEY = "0x4AAAAAAFhH5hv5sjWl6VCi";

   const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileWidgetRef = useRef(null);

   useEffect(() => {
    // Render invisible Turnstile on mount
    const container = document.getElementById("turnstile-container");
    if (!container || typeof window.turnstile === "undefined") return;

    const widgetId = window.turnstile.render(container, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: "light",
      callback: (token) => setTurnstileToken(token),
      "expired-callback": () => setTurnstileToken(""),
      "error-callback": () => setTurnstileToken(""),
    });
    turnstileWidgetRef.current = widgetId;

    return () => {
      if (turnstileWidgetRef.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetRef.current);
      }
    };
  }, []);

  const API = "https://simplix.co.za/api";

  const [tab, setTab] = useState("balance");

  // Auth
  const [meterNumber, setMeterNumber] = useState("");
  const [authMeter, setAuthMeter] = useState("");
  const [meterId, setMeterId] = useState("");
  const [sessionToken, setSessionToken] = useState(
    () => localStorage.getItem("public_meter_session") || ""
  );
  const [authed, setAuthed] = useState(Boolean(sessionToken));
  const [authBusy, setAuthBusy] = useState(false);

  // Relay
  const [relayState, setRelayState] = useState("");

  // Balance
  const [balanceBusy, setBalanceBusy] = useState(false);
  const [remainingKwh, setRemainingKwh] = useState(null);
  const [asOf, setAsOf] = useState("");
  const [balanceMsg, setBalanceMsg] = useState("");

  // Load token
  const [confirmMeter, setConfirmMeter] = useState("");
  const [tokenToLoad, setTokenToLoad] = useState("");
  const [loadBusy, setLoadBusy] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");

  // Messages
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  // Progress
  const [authProg, setAuthProg] = useState({ show: false, pct: 0, stage: "", details: "" });
  const [balProg, setBalProg] = useState({ show: false, pct: 0, stage: "", details: "" });
  const [loadProg, setLoadProg] = useState({ show: false, pct: 0, stage: "", details: "" });

  const authTimerRef = useRef(null);
  const balTimerRef = useRef(null);
  const loadTimerRef = useRef(null);

  // --- Progress helpers ---
  const startProgress = useCallback((setProg, timerRef, initialStage, details = "") => {
    setProg({ show: true, pct: 6, stage: initialStage, details });
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setProg((p) => {
        if (!p.show) return p;
        const next = Math.min(92, (p.pct || 0) + (p.pct < 40 ? 6 : p.pct < 70 ? 3 : 1));
        return { ...p, pct: next };
      });
    }, 450);
  }, []);

  const setProgressStage = useCallback((setProg, stage, pct = null, details = null) => {
    setProg((p) => ({
      ...p,
      stage: stage ?? p.stage,
      pct: pct == null ? p.pct : Math.max(0, Math.min(99, pct)),
      details: details == null ? p.details : details,
      show: true,
    }));
  }, []);

  const finishProgress = useCallback((setProg, timerRef, ok = true) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setProg((p) => ({ ...p, pct: 100, show: true }));
    setTimeout(() => {
      setProg((p) => ({
        ...p,
        show: false,
        pct: ok ? 0 : p.pct,
        stage: "",
        details: "",
      }));
    }, 650);
  }, []);

  useEffect(() => {
    return () => {
      if (authTimerRef.current) clearInterval(authTimerRef.current);
      if (balTimerRef.current) clearInterval(balTimerRef.current);
      if (loadTimerRef.current) clearInterval(loadTimerRef.current);
    };
  }, []);

  // --- Input helpers ---
  const normMeter = (s) => (s || "").trim();
  const normToken = (s) => (s || "").replace(/\s+/g, "").trim();
  const canAuth = useMemo(() => normMeter(meterNumber).length >= 6, [meterNumber]);

  const resetMessages = () => {
    setError("");
    setOk("");
    setBalanceMsg("");
    setLoadMsg("");
  };

  const getRelayOffMessage = (kwh) => {
    const n = Number(kwh);
    if (Number.isFinite(n) && n <= 0) {
      return "⚠️ This meter's relay is OFF because the meter has 0 residual kWh or no available credit. Please load a token to restore supply.";
    }
    return "⚠️ This meter's relay is OFF. Please contact your Managing Agent for assistance.";
  };

  const applyRelayFromResponse = (json, opts = {}) => {
    const rs = (json?.relay_state || json?.relayState || "").toString().toLowerCase();
    if (!rs) return;
    setRelayState(rs);

    const responseKwh = Number(json?.remaining_kwh);
    const fallbackKwh = opts?.fallbackKwh !== undefined ? opts.fallbackKwh : remainingKwh;
    const kwhToUse = Number.isFinite(responseKwh) ? responseKwh : fallbackKwh;

    if (rs === "off") {
      setError(getRelayOffMessage(kwhToUse));
      setOk("");
    }
  };

  const clearSession = () => {
    setSessionToken("");
    setAuthed(false);
    setAuthMeter("");
    setMeterId("");
    setRemainingKwh(null);
    setAsOf("");
    setConfirmMeter("");
    setTokenToLoad("");
    setRelayState("");
    localStorage.removeItem("public_meter_session");
    localStorage.removeItem("public_meter_number");
    localStorage.removeItem("public_meter_id");
  };

  useEffect(() => {
    const savedMeter = localStorage.getItem("public_meter_number") || "";
    const savedId = localStorage.getItem("public_meter_id") || "";
    if (savedMeter && !meterNumber) setMeterNumber(savedMeter);
    if (savedMeter && authed && !authMeter) setAuthMeter(savedMeter);
    if (savedId && !meterId) setMeterId(savedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Error helpers ---
  const classifyFailure = ({ status, err }) => {
    if (err) return "temporary";
    const s = Number(status || 0);
    if (!s) return "temporary";
    if (s >= 500 || s === 408 || s === 429 || s === 423) return "temporary";
    return "permanent";
  };

  const tipForClass = (cls) =>
    cls === "temporary"
      ? "Tip: Please retry in 5 minutes. If the error persists, contact support."
      : "Tip: Please contact support.";

  const tipOverrideForRechargeErrors = (msg) => {
    const m = (msg || "").toLowerCase();
    if (m.includes("recharge failure") && m.includes("token has been used")) {
      return "Tip: This token has already been used to recharge. Please confirm whether the token is correct.";
    }
    if (m.includes("recharge failure") && m.includes("token error")) {
      return "Tip: Please confirm the format of the token (20 digits) and try again.";
    }
    return null;
  };

  const stripErrorAndTipLines = (s) => {
    let out = String(s || "").trim();
    out = out.replace(/^Error:\s*/i, "");
    out = out.replace(/\n+Tip:\s*[^\n]*/gi, "");
    return out.trim();
  };

  const buildFailure = ({ status, backendMsg, err, json, classification } = {}) => {
    const cls = classification || classifyFailure({ status, err });
    const serverText =
      backendMsg ||
      json?.error ||
      json?.message ||
      (json?.detail ? String(json.detail) : "") ||
      "";
    const clientText = err?.message ? String(err.message) : "";
    const cleaned = stripErrorAndTipLines(serverText || clientText || "Something went wrong.");
    const overrideTip = tipOverrideForRechargeErrors(cleaned);
    const tip = overrideTip || tipForClass(cls);
    return `Error: ${cleaned}\n${tip}`;
  };

  // --- Fetch helper ---
  const publicFetch = useCallback(async (path, options = {}) => {
    const stored = localStorage.getItem("public_meter_session") || "";
    const sess = sessionToken || stored;

    const headers = {
      ...(options.headers || {}),
      "Content-Type": "application/json",
    };
    if (sess) headers.Authorization = `Bearer ${sess}`;
    if (turnstileToken) headers["X-Turnstile-Token"] = turnstileToken;

    let body = options.body;
    if (body && typeof body === "string") {
      try {
        const parsed = JSON.parse(body);
        if (!parsed.session_token && sess) parsed.session_token = sess;
        body = JSON.stringify(parsed);
      } catch (_) {}
    }

    let res;
    try {
      res = await fetch(`${API}${path}`, { ...options, headers, body });
    } catch (err) {
      throw new Error(
        buildFailure({ status: "", backendMsg: "", err, json: {} })
      );
    }

    let json = {};
    try {
      json = await res.json();
    } catch (_) {
      json = {};
    }

    if (!res.ok || json?.ok === false) {
      throw new Error(
        buildFailure({
          status: res.status,
          backendMsg: json?.error || json?.message,
          err: null,
          json,
        })
      );
    }

    return json;
  },  [sessionToken, turnstileToken]);

  // --- Actions ---
  const authenticateMeter = async () => {
    resetMessages();
    setAuthBusy(true);
    const m = normMeter(meterNumber);

    startProgress(setAuthProg, authTimerRef, "Starting authentication…", `meter=${m || "—"}`);

    try {
      setProgressStage(setAuthProg, "Validating input…", 12);
      if (!m) throw new Error("Error: Please enter your meter number.\nTip: Please contact support.");

      setProgressStage(setAuthProg, "Sending authentication request…", 22);

      let res;
    try {
      const headers = { "Content-Type": "application/json" };
      if (turnstileToken) headers["X-Turnstile-Token"] = turnstileToken;
      res = await fetch(`${API}/public/meters/auth/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ meter_number: m }),
      });
    } catch (err) {
      throw new Error(buildFailure({ status: "", backendMsg: "", err, json: {} }));
    }

      setProgressStage(setAuthProg, "Processing server response…", 55);
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(
          buildFailure({
            status: res.status,
            backendMsg: json?.error || json?.message,
            err: null,
            json,
          })
        );
      }

      setProgressStage(setAuthProg, "Saving session…", 72);

      const sess = json?.session_token || "";
      if (!sess) {
        throw new Error(
          buildFailure({
            status: res.status,
            backendMsg: "Authentication succeeded but no session token was returned.",
            err: null,
            json,
            classification: "permanent",
          })
        );
      }

      localStorage.setItem("public_meter_session", sess);
      localStorage.setItem("public_meter_number", m);
      if (json?.meter_id) localStorage.setItem("public_meter_id", json.meter_id);

      setSessionToken(sess);
      setAuthed(true);
      setAuthMeter(m);
      setMeterId(json?.meter_id || "");
      setConfirmMeter(m);

      setProgressStage(setAuthProg, "Checking relay status…", 86);
      applyRelayFromResponse(json);

      if ((json?.relay_state || "").toString().toLowerCase() === "off") {
        setError(
          "⚠️ This meter's relay is OFF. You can still check the residual kWh or load a token. If the residual is 0, please load a token. Otherwise, contact your Managing Agent for assistance."
        );
      } else {
        setOk("Meter authenticated successfully.");
      }

      finishProgress(setAuthProg, authTimerRef, true);
    } catch (e) {
      setError(String(e.message || e));
      clearSession();
      finishProgress(setAuthProg, authTimerRef, false);
    } finally {
      setAuthBusy(false);
    }
  };

  const fetchResidual = async () => {
    resetMessages();
    setBalanceBusy(true);
    const m = authMeter || normMeter(meterNumber);

    startProgress(setBalProg, balTimerRef, "Starting residual read…", `meter=${m || "—"}`);

    try {
      setProgressStage(setBalProg, "Validating session…", 14);
      if (!authed)
        throw new Error("Error: Please authenticate your meter number first.\nTip: Please contact support.");
      if (!m)
        throw new Error("Error: Missing meter number.\nTip: Please contact support.");

      setProgressStage(setBalProg, "Requesting residual kWh from meter…", 34);

      const json = await publicFetch("/public/meters/residual/", {
        method: "POST",
        body: JSON.stringify({ meter_number: m, session_token: sessionToken }),
      });

      setProgressStage(setBalProg, "Parsing residual value…", 62);

      const kwh = Number.isFinite(Number(json?.remaining_kwh))
        ? Number(json.remaining_kwh)
        : null;

      setRemainingKwh(kwh);
      setAsOf(json?.as_of || json?.asOf || "");
      setBalanceMsg("Residual kWh retrieved.");

      setProgressStage(setBalProg, "Checking relay status…", 82);
      applyRelayFromResponse(json, { fallbackKwh: kwh });

      if ((json?.relay_state || "").toString().toLowerCase() !== "off") {
        setOk("Success.");
      }

      finishProgress(setBalProg, balTimerRef, true);
    } catch (e) {
      const msg = String(e.message || e);
      const finalMsg = msg.startsWith("Error:")
        ? msg
        : buildFailure({ status: "", backendMsg: msg, err: null, json: {}, classification: "permanent" });
      setError(finalMsg);
      finishProgress(setBalProg, balTimerRef, false);
    } finally {
      setBalanceBusy(false);
    }
  };

  const loadToken = async () => {
    resetMessages();
    setLoadBusy(true);

    const m = authMeter || normMeter(meterNumber);
    const mConfirm = normMeter(confirmMeter);
    const token = normToken(tokenToLoad);

    startProgress(setLoadProg, loadTimerRef, "Starting token load…", `meter=${mConfirm || m || "—"}`);

    try {
      setProgressStage(setLoadProg, "Validating session & inputs…", 14);

      if (!authed) throw new Error("Error: Please authenticate your meter number first.");
      if (!mConfirm) throw new Error("Error: Please confirm the meter number.");
      if (mConfirm !== m) {
        throw new Error("Error: Confirmed meter number does not match the authenticated meter.");
      }
      if (!token) throw new Error("Error: Please enter the token to load.");
      if (!/^\d{20}$/.test(token)) {
        throw new Error("Error: Token must be exactly 20 digits.\nTip: Please check your token and try again.");
      }

      setProgressStage(setLoadProg, "Sending token to backend…", 32);

      const json = await publicFetch("/public/meters/load-token/", {
        method: "POST",
        body: JSON.stringify({
          meter_number: mConfirm,
          token,
          session_token: sessionToken,
        }),
      });

      setProgressStage(setLoadProg, "Updating residual kWh from response…", 58);

      const kwh = Number.isFinite(Number(json?.remaining_kwh))
        ? Number(json.remaining_kwh)
        : null;

      if (kwh != null) {
        setRemainingKwh(kwh);
        setAsOf(json?.as_of || json?.asOf || "");
      }

      setProgressStage(setLoadProg, "Checking relay status…", 76);
      applyRelayFromResponse(json, { fallbackKwh: kwh });

      setTokenToLoad("");
      setLoadMsg("Token processed.");

      if ((json?.relay_state || "").toString().toLowerCase() !== "off") {
        setOk("Success.");
      }

      setProgressStage(setLoadProg, "Finalizing…", 92);

      if (kwh == null) {
        setProgressStage(setLoadProg, "Residual not returned — fetching residual…", 94);
        await fetchResidual();
      }

      finishProgress(setLoadProg, loadTimerRef, true);
    } catch (e) {
      const raw = String(e?.message || e || "");
      const cleaned = stripErrorAndTipLines(raw);
      const finalMsg = buildFailure({
        status: "",
        backendMsg: cleaned,
        err: null,
        json: {},
        classification: "permanent",
      });
      setError(finalMsg);
      finishProgress(setLoadProg, loadTimerRef, false);
    } finally {
      setLoadBusy(false);
    }
  };

  // --- UI: Progress bar ---
  const ProgressBar = ({ prog }) => {
    if (!prog?.show) return null;
    return (
      <div
        className="card"
        style={{ padding: 12, marginTop: 10, border: "1px solid rgba(0,0,0,0.08)", background: "rgba(0,0,0,0.02)" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontWeight: 800 }}>{prog.stage || "Working…"}</div>
          <div className="badge" style={{ fontWeight: 900 }}>
            {Math.min(100, Math.max(0, Math.round(prog.pct || 0)))}%
          </div>
        </div>
        <div style={{ height: 10, borderRadius: 999, background: "rgba(0,0,0,0.10)", overflow: "hidden", marginTop: 10 }}>
          <div
            style={{
              height: "100%",
              width: `${Math.min(100, Math.max(0, prog.pct || 0))}%`,
              borderRadius: 999,
              background: "rgba(0,0,0,0.55)",
              transition: "width 260ms ease",
            }}
          />
        </div>
        {prog.details ? (
          <div className="sub" style={{ marginTop: 8, opacity: 0.9, whiteSpace: "pre-wrap" }}>
            {prog.details}
          </div>
        ) : null}
      </div>
    );
  };

  // --- Render ---
  return (
    <div className={styles.page}>
      <div id="turnstile-container" style={{ display: "none" }} />
      <div className={`${styles.section} ${styles.meterDetailSection}`}>
        {/* HERO */}
        <div className={styles.meterDetailHero}>
          <div>
            <p className={styles.sectionEyebrow}>Public Access</p>
            <h1 className={styles.h2}>Meter Balance &amp; Token</h1>
            <p className={styles.sectionIntro}>
              Check residual kWh or load a prepaid token without logging in.
            </p>
          </div>
        </div>

        {/* AUTH PANEL */}
        <div className={styles.meterDetailPanel}>
          <div className={styles.meterDetailPanelHeader}>
            <div>
              <p className={styles.sectionEyebrow}>Authentication</p>
              <h2>Authenticate meter</h2>
              <p className={styles.meterPanelText}>
                {authed
                  ? `Authenticated meter: ${authMeter}`
                  : "Authenticate before accessing residual kWh or loading tokens."}
              </p>
            </div>
            {authed && relayState && (
              <span className={styles.badge}>Relay: {relayState.toUpperCase()}</span>
            )}
          </div>

          <div className={styles.publicMeterAuthGrid}>
            <input
              className={styles.authInput}
              placeholder="Enter meter number"
              value={meterNumber}
              onChange={(e) => setMeterNumber(e.target.value)}
              disabled={authed}
              maxLength={30}
              autoComplete="off"
              spellCheck={false}
            />
            {!authed ? (
              <button
                className={styles.primaryBtn}
                onClick={authenticateMeter}
                disabled={!canAuth || authBusy}
              >
                {authBusy ? "Authenticating…" : "Authenticate"}
              </button>
            ) : (
              <button className={styles.inlineBtn} onClick={clearSession}>
                Change meter
              </button>
            )}
          </div>

          <ProgressBar prog={authProg} />
        </div>

        {/* TABS */}
        <div className={styles.publicMeterTabs}>
          <button
            className={`${styles.publicMeterTabBtn} ${tab === "balance" ? styles.publicMeterTabActive : ""}`}
            onClick={() => setTab("balance")}
            disabled={!authed}
          >
            ⚡ Residual kWh
          </button>
          <button
            className={`${styles.publicMeterTabBtn} ${tab === "load" ? styles.publicMeterTabActive : ""}`}
            onClick={() => setTab("load")}
            disabled={!authed}
          >
            🧾 Load Token
          </button>
        </div>

        {/* GLOBAL MSGS */}
        {error && <div className={styles.authError}>{error}</div>}
        {ok && <div className={styles.authSuccess}>{ok}</div>}

        {/* RESIDUAL */}
        {tab === "balance" && (
          <div className={styles.meterDetailPanel}>
            <div className={styles.meterDetailPanelHeader}>
              <div>
                <p className={styles.sectionEyebrow}>Residual</p>
                <h2>Residual kWh</h2>
              </div>
              <div className={styles.meterChartActions}>
                <button
                  className={styles.primaryBtn}
                  onClick={fetchResidual}
                  disabled={!authed || balanceBusy}
                >
                  {balanceBusy ? "Retrieving…" : "Get residual"}
                </button>
                <button
                  className={styles.inlineBtn}
                  onClick={() => {
                    setRemainingKwh(null);
                    setAsOf("");
                    setBalanceMsg("");
                    resetMessages();
                  }}
                >
                  Clear
                </button>
              </div>
            </div>

            <ProgressBar prog={balProg} />

            <div className={styles.publicKwhCard}>
              <span>Residual kWh</span>
              <strong>
                {remainingKwh == null ? "—" : `${remainingKwh.toFixed(2)} kWh`}
              </strong>
              {asOf && <p>As of: {asOf}</p>}
              {balanceMsg && <p>{balanceMsg}</p>}
            </div>
          </div>
        )}

        {/* TOKEN */}
        {tab === "load" && (
          <div className={styles.meterDetailPanel}>
            <div className={styles.meterDetailPanelHeader}>
              <div>
                <p className={styles.sectionEyebrow}>Recharge</p>
                <h2>Load Token</h2>
              </div>
            </div>

            <div className={styles.authForm}>
              <input
                className={styles.authInput}
                placeholder="Confirm meter number"
                value={confirmMeter}
                onChange={(e) => setConfirmMeter(e.target.value)}
                maxLength={30}
                autoComplete="off"
                spellCheck={false}
              />
              <input
                className={styles.authInput}
                placeholder="Enter token (20 digits)"
                value={tokenToLoad}
                onChange={(e) => setTokenToLoad(e.target.value)}
                maxLength={60}
                autoComplete="off"
                spellCheck={false}
              />
              <div className={styles.publicTokenActions}>
                <button
                  className={styles.primaryBtn}
                  onClick={loadToken}
                  disabled={loadBusy || !authed}
                >
                  {loadBusy ? "Loading…" : "Load token"}
                </button>
                <button
                  className={styles.inlineBtn}
                  onClick={() => {
                    setConfirmMeter(authMeter || "");
                    setTokenToLoad("");
                    setLoadMsg("");
                    resetMessages();
                  }}
                >
                  Reset
                </button>
              </div>

              <ProgressBar prog={loadProg} />

              <div className={styles.publicKwhCard}>
                <span>Residual after load</span>
                <strong>
                  {remainingKwh == null ? "—" : `${remainingKwh.toFixed(2)} kWh`}
                </strong>
                {asOf && <p>As of: {asOf}</p>}
                {loadMsg && <p>{loadMsg}</p>}
              </div>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className={styles.authHelp}>
          <span className={styles.micro}>Need help? support@simplex.co.za</span>
          <button className={styles.inlineBtn} onClick={clearSession}>
            End session
          </button>
        </div>
      </div>
    </div>
  );
}